/**
 * One filter for the project's repositories: "Whole project · web · api · lib". A single select (never a row of
 * buttons) shared by the board, the queue, the plans and the risks. `ALL_ROOTS` is the whole project.
 */

import { useState } from 'react';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { ChoiceSelect } from './ui.tsx';

/** A ListBox id for "the whole project": an empty string is not a reliable id. */
export const ALL_ROOTS = '__all';

/** The project's repositories by name (the registration's name, else the id), for the filter and for words. */
export function useRootNames(projectId: string): Record<string, string> {
  const map = useLoad(() => routes.projectMap(projectId).catch(() => undefined), [projectId]);
  return Object.fromEntries((map.data?.roots ?? []).map((r) => [r.rootId, r.name ?? r.rootId]));
}

export function useRootFilter(projectId: string) {
  const [root, setRoot] = useState<string>(ALL_ROOTS);
  const names = useRootNames(projectId);
  /** Whether a repository id passes the filter (the project's own, root "", passes only for the whole project). */
  const passes = (rootId: string) => root === ALL_ROOTS || rootId === root;
  return { root, setRoot, names, passes, control: <RootFilter names={names} value={root} onChange={setRoot} /> };
}

export function RootFilter({ names, value, onChange }: { names: Record<string, string>; value: string; onChange: (v: string) => void }) {
  const ids = Object.keys(names);
  // Nothing to choose between with one repository or none.
  if (ids.length < 2) return null;
  return <ChoiceSelect label="Repository" className="w-fit min-w-44 max-w-full" value={value} options={{ [ALL_ROOTS]: `Whole project · ${ids.map((i) => names[i]).join(' · ')}`, ...Object.fromEntries(ids.map((i) => [i, names[i]!])) }} onChange={onChange} />;
}
